/**
 * PATH: packages/email-hub/src/feature/pages/inbox-page/InboxResultsSurface.tsx
 * FILE_ID: EMAIL-INBOX-RESULTS-SURFACE-001
 * PURPOSE: Preserve category, state, row, grouping, and pagination behavior.
 */

import { AlertCircle,RefreshCw } from 'lucide-react'
import type { MouseEvent } from 'react'
import { deriveEmailListState,EmailListSkeleton,EmailListStatePanel,shouldFlagIdentityStale } from '../../components/EmailListStatePanel'
import { InboxSweepBanner } from '../../components/InboxSweepBanner'
import { LiveSearchResults } from '../../components/LiveSearchResults'
import ParkedReviewDigest from '../../components/ParkedReviewDigest'
import type { Email } from '../../hooks/useEmails'
import { InboxEmailRows } from './InboxEmailRows'
import type { InboxPageController } from './useInboxPageController'

export function InboxResultsSurface({
  vm,
  handleContextMenu,
}: {
  vm: InboxPageController
  handleContextMenu: (event: MouseEvent, email: Email) => void
}) {
  const {
    isInboxFolder,
    debouncedSearchQuery,
    selectedCategories,
    rightTab,
    listRef,
    loading,
    pendingNewQuery,
    error,
    hasMore,
    loadMore,
    refetch,
    listLoading,
    syncStatus,
    triggerSync,
    handleIdentityReload,
    isAutoSyncing,
    stats,
    statsError,
    refetchStats,
    handleEmailClick,
    dedupedEmails,
    clearWatchdogClock,
    loadWatchdogTripped,
    setLoadWatchdogTripped,
    hasActiveQuery,
  } = vm
  return (
    <>
        {rightTab === 'inbox' && (
        <>
        {/* Stats fetch failed. Loud banner only when we have NO last-good
            counts (empty badges). When last-good stats still paint, stay
            quiet — constant amber alarmed operators even though badges
            were usable (2026-07-31). Retry remains available via Sync. */}
        {isInboxFolder && statsError && !stats && (
          <div
            className="px-3 py-1 text-[11px] text-amber-600 dark:text-amber-400 bg-amber-500/5 border-b border-border flex items-center gap-2"
            data-testid="inbox-stats-stale-banner"
          >
            <span className="flex-1 min-w-0 truncate">
              Counts may be out of date (failed to refresh).
            </span>
            <button
              type="button"
              onClick={() => { void refetchStats() }}
              className="shrink-0 font-medium underline-offset-2 hover:underline"
            >
              Retry
            </button>
          </div>
        )}

        {/* Email List. ``overflow-anchor`` is explicit so a poll tick
            that PREPENDS new mail doesn't shove the operator's reading
            position down - the browser pins the scroll to the anchored
            row below the insertion. */}
        <div ref={listRef} className="flex-1 overflow-y-auto" style={{ overflowAnchor: 'auto' }}>
          {isInboxFolder && (
            <InboxSweepBanner
              onReverted={() => {
                refetch()
                refetchStats()
              }}
            />
          )}
          {error && (
            // BUG-2026-05-23-EMAIL-STUCK-LOADING: error banner now
            // carries an explicit Retry button. Previously the only
            // recovery path was to find the header "Sync" button or
            // hard-refresh the tab — both non-obvious when the rows
            // area is empty + the operator is staring at a red banner
            // wondering what to do. The Retry click calls the
            // useEmails-exposed refetch which goes through the same
            // inflight-throttled runEmailsFetch path, so a fresh
            // 25s timeout window starts and the UI converges on
            // either rows or a fresh error.
            <div
              data-quotech-inbox-error-banner
              className="mx-3 mt-3 p-2.5 bg-destructive/10 border border-destructive/20 rounded-md flex items-center gap-2"
            >
              <AlertCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
              <span className="text-xs text-destructive flex-1 min-w-0">{error}</span>
              <button
                type="button"
                onClick={() => { refetch() }}
                className="text-xs font-medium text-destructive underline-offset-2 hover:underline shrink-0"
                aria-label="Retry loading inbox"
              >
                Retry
              </button>
            </div>
          )}
          {/* Result-count / "Searching…" strip. While a NEW search/filter
              query is settling (`pendingNewQuery`) we say "Searching…" so the
              stale rows underneath don't read as the (wrong) result; once it
              lands we show how many matched + a "+" when more pages exist, so
              the operator can tell an empty/short result from a truncated one.
              Only shown when a search or filter is actually active. */}
          {hasActiveQuery && (pendingNewQuery || dedupedEmails.length > 0) && (
            <div className="px-3 py-1 border-b border-border/50 bg-muted/10 flex items-center gap-1.5 text-[11px] text-muted-foreground select-none">
              {pendingNewQuery ? (
                <>
                  <RefreshCw className="h-3 w-3 animate-spin" />
                  <span>Searching…</span>
                </>
              ) : (
                <span>
                  {dedupedEmails.length}{hasMore ? '+' : ''}{' '}
                  {dedupedEmails.length === 1 ? 'result' : 'results'}
                </span>
              )}
            </div>
          )}
          {/* Triage v6 — ONE "AI parked N for review" digest instead of
              a repeated amber prose sentence on every parked email
              (operator 2026-07-09: "that low confidence is just
              spamming here"). Renders nothing when nothing is parked. */}
          <ParkedReviewDigest
            emails={dedupedEmails}
            onOpen={(e) => { void handleEmailClick(e) }}
            count={vm.stats?.needs_review}
            active={vm.needsReviewOnly}
            onToggleFilter={vm.toggleNeedsReviewOnly}
          />
          {dedupedEmails.length === 0 ? (
            // ARC EMAIL_HUB_LOADING_RESILIENCE — the list pane no longer
            // hangs on a bare indefinite "Loading…" nor claims "No emails
            // yet" while a backfill sync is mid-flight. `deriveEmailListState`
            // (pure I→O, unit-tested) picks exactly one state from the
            // rows/loading/sync/watchdog signals; the watchdog guarantees a
            // wedged spinner degrades to a retryable "stuck" state.
            // BUG-2026-05-14-EMAIL-EMPTY-CRAMPED is preserved: the panel
            // uses `min-h-full` + centred flex so it fills the scroll area
            // instead of pinning to the top in a fixed-height box.
            (() => {
              const listState = deriveEmailListState({
                rowCount: dedupedEmails.length,
                // BUG-2026-06-12-SEARCH-FALSE-EMPTY: an in-flight NEW
                // search/filter query must read as loading (skeleton),
                // never as "No emails match your filters".
                loading: listLoading,
                isSyncing: isAutoSyncing,
                watchdogTripped: loadWatchdogTripped,
                hasFilter: !!(selectedCategories.length || debouncedSearchQuery),
                // §D — the latest sync FAILED (token/connection) and
                // nothing is visible: surface the error, not a spinner.
                // Also trips on the per-mailbox health flag, which the
                // zombie reaper now stamps (§F) when a worker dies on a
                // first import — so a crashed import reads as an error,
                // not a never-ending spinner.
                syncError:
                  syncStatus?.latest?.status === 'error' ||
                  !!syncStatus?.summary?.any_with_error,
                // §D — contradiction heuristic for the pinTabIdentity
                // bug: the most recent sync COMPLETED and reported
                // writing rows, yet the list settled empty here. Strong
                // signal the list query is running as the wrong user.
                // INBOX-ONLY: a folder (Sent/Archived/Starred) is
                // legitimately empty even when the mailbox synced lots of
                // mail (e.g. zero starred), so the heuristic must not fire
                // there — else every empty folder reads as a scary
                // "session changed" error (BUG-2026-06-17-FOLDER-FRAME-
                // FALSE-IDENTITY-STALE). Gated on no filter + not mid-sync.
                identityStale: shouldFlagIdentityStale({
                  isInboxFolder,
                  listLoading,
                  isSyncing: isAutoSyncing,
                  rowCount: dedupedEmails.length,
                  hasFilter: !!(
                    selectedCategories.length || debouncedSearchQuery
                  ),
                  latestSyncSucceeded:
                    syncStatus?.latest?.status === 'success',
                  syncedCount: syncStatus?.latest?.emails_synced ?? 0,
                }),
              })
              // Operator 2026-06-01: "just the visual frame first". For the
              // in-flight states (first page loading, or syncing with nothing
              // yet) paint the SKELETON LIST FRAME immediately instead of a
              // centred spinner that withholds the whole list. The genuine
              // dead-ends — 'stuck' / 'sync-error' / 'identity-stale' /
              // 'empty' / 'empty-filtered' — still get the messaged panel
              // (retry/reload affordance + "no mail" copy), never an
              // indefinite scaffold.
              if (listState === 'loading' || listState === 'syncing') {
                return <EmailListSkeleton count={9} />
              }
              return (
                <EmailListStatePanel
                  state={
                    listState as Exclude<
                      ReturnType<typeof deriveEmailListState>,
                      'list'
                    >
                  }
                  errorDetail={syncStatus?.latest?.error_message ?? null}
                  syncedSoFar={syncStatus?.active_sync?.emails_synced ?? 0}
                  onReload={handleIdentityReload}
                  onRetry={() => {
                    // Cold-start dead-end (§A): a plain refetch won't conjure
                    // rows the worker never produced, so escalate to a
                    // force-full sync (heals a stale delta cursor / kicks a
                    // never-started import) AND refetch. Reset BOTH the in-memory
                    // flag and the persisted across-remount clock so this attempt
                    // gets a full fresh window before the pane re-escalates.
                    setLoadWatchdogTripped(false)
                    clearWatchdogClock()
                    void triggerSync(90, { forceFull: true })
                    refetch()
                  }}
                />
              )
            })()
          ) : (
            <InboxEmailRows
              vm={vm}
              handleContextMenu={handleContextMenu}
            />
          )}
          <LiveSearchResults
            offered={vm.liveSearch.offered}
            status={vm.liveSearch.status}
            data={vm.liveSearch.data}
            error={vm.liveSearch.error}
            onTrigger={vm.liveSearch.trigger}
            onOpenLocal={vm.setSelectedEmailId}
          />
          {/* Audit P0-4 (Arc 2) — paginated inbox needs an explicit
              "load more" affordance so the operator can reach mail
              past the first 200 rows. We render this OUTSIDE the
              dedup map so it sits at the bottom of the visible list,
              not on every row. ``hasMore`` flips false once the
              cursor reaches the end of the queryset; the button is
              disabled while a fetch is in flight to prevent
              double-firing. */}
          {dedupedEmails.length > 0 && hasMore && (
            <div className="flex justify-center py-3 border-t border-border/40">
              <button
                type="button"
                onClick={() => loadMore()}
                disabled={loading}
                className="text-xs px-3 py-1.5 rounded-md border border-border/60 bg-background hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Load older emails"
                title="Fetch the next page of older emails from the server"
              >
                {loading ? 'Loading…' : 'Load older emails'}
              </button>
            </div>
          )}
        </div>
        </>
        )}

    </>
  )
}
